import { ShopBadgeType, ShopDisplaySection } from '../models/dtos/ShopItemDto';
import { injectShopResponsiveHtml } from './shopResponsiveHtml';

const FALLBACK_IMAGE_URL =
    'https://qbetapp.info/uploads_api/ads-generated/52342010-45fe-41b3-8edf-5f7a823118fc.png';

export interface ShopHtmlPreviewContext {
    iconUrl?: string;
    storeCode: string;
    title: string;
    description: string;
    buyLabel: string;
    betcoins: number;
    visualPrice: string;
    oldPrice: string;
    discountPercent: number;
    displaySection: ShopDisplaySection;
    badgeType?: ShopBadgeType;
    displayLabel: string;
    packageDays?: number;
    effectCoef?: number;
    effectDuration?: number;
    noAds: boolean;
    offerExpiresAtMillis?: number;
    popular?: boolean;
    unitPriceLabel?: string;
    multiplier?: string;
    isVideoReward?: boolean;
    isVideoRewardAvailable?: boolean;
    videoTitle?: string;
    videoSubtitle?: string;
    language?: string;
    firstPurchaseEyebrow?: string;
}

export const SHOP_HTML_PLACEHOLDERS = [
    '{{betcoins}}', '{{iconUrl}}', '{{oldPrice}}', '{{newPrice}}',
    '{{eyebrow}}', '{{headline}}', '{{subtitle}}', '{{title}}',
    '{{buyLabel}}', '{{storeCode}}', '{{section}}', '{{timer}}',
    '{{badge}}', '{{discount}}', '{{bulletsHtml}}', '{{packageDays}}',
    '{{unitPrice}}', '{{cornerRibbonText}}', '{{videoTitle}}', '{{videoSubtitle}}',
    '{{borderColor}}', '{{titleAccent}}', '{{cardOpacity}}', '{{ctaDisabled}}',
    '{{timerDisplay}}', '{{badgeDisplay}}', '{{discountDisplay}}', '{{oldPriceDisplay}}',
    '{{daysDisplay}}', '{{unitPriceDisplay}}', '{{popularRibbonDisplay}}',
    '{{cornerRibbonDisplay}}', '{{cornerRibbonClass}}', '{{heroDisplay}}',
] as const;

function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function escapeJs(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function formatCoins(amount: number): string {
    if (amount <= 0) return '';
    return amount.toLocaleString('en-US');
}

export function formatOfferCountdown(expiresAtMillis?: number): string | null {
    if (expiresAtMillis == null) return null;
    const diff = expiresAtMillis - Date.now();
    if (diff <= 0) return null;
    const totalSeconds = Math.floor(diff / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [hours, minutes, seconds].map(n => String(n).padStart(2, '0')).join(':');
}

function cornerRibbon(badgeType?: ShopBadgeType): { text: string; visible: boolean; cssClass: string } {
    switch (badgeType) {
        case 'BONUS_PCT_20': return { text: '+20%', visible: true, cssClass: 'ribbon-green' };
        case 'BONUS_PCT_50': return { text: '+50%', visible: true, cssClass: 'ribbon-blue' };
        case 'NEW': return { text: 'NEW', visible: true, cssClass: 'ribbon-silver' };
        default: return { text: '', visible: false, cssClass: '' };
    }
}

const FIRST_PURCHASE_EYEBROW_BY_LANG: Record<string, string> = {
    en: 'First Purchase Offer',
    ro: 'Ofertă prima achiziție',
    de: 'Erstkauf-Angebot',
    fr: 'Offre premier achat',
    es: 'Oferta primera compra',
    it: 'Offerta primo acquisto',
    pt: 'Oferta primeira compra',
    ru: 'Предложение при первой покупке',
    uk: 'Пропозиція першої покупки',
    tr: 'İlk satın alma teklifi',
    ar: 'عرض الشراء الأول',
    hi: 'पहली खरीद ऑफर',
};

export function localizedFirstPurchaseEyebrow(language?: string): string {
    const lang = language?.toLowerCase() ?? 'en';
    return FIRST_PURCHASE_EYEBROW_BY_LANG[lang] ?? FIRST_PURCHASE_EYEBROW_BY_LANG.en;
}

function eyebrowFor(ctx: ShopHtmlPreviewContext): string {
    if (ctx.displayLabel) return ctx.displayLabel;
    if (ctx.displaySection === 'FIRST_PURCHASE') {
        return ctx.firstPurchaseEyebrow || localizedFirstPurchaseEyebrow(ctx.language);
    }
    return ctx.title;
}

function headlineFor(ctx: ShopHtmlPreviewContext): string {
    if (ctx.displaySection === 'FIRST_PURCHASE') {
        return ctx.multiplier || (ctx.description && ctx.description !== ctx.displayLabel ? ctx.description : '') || ctx.title;
    }
    if (ctx.title) return ctx.title;
    return ctx.description;
}

function subtitleFor(ctx: ShopHtmlPreviewContext): string {
    if (ctx.displaySection === 'FIRST_PURCHASE') return '';
    if (ctx.displaySection === 'BUNDLES' || ctx.displaySection === 'COIN_PACKS') return ctx.description;
    return ctx.description;
}

function badgeLabel(badge?: ShopBadgeType): string {
    switch (badge) {
        case 'POPULAR': return 'POPULAR';
        case 'NEW': return 'NEW';
        case 'BONUS_PCT_20': return '+20%';
        case 'BONUS_PCT_50': return '+50%';
        default: return '';
    }
}

function buildDailyDealBullets(ctx: ShopHtmlPreviewContext): string {
    const parts: string[] = [];
    if (ctx.effectCoef != null && ctx.effectDuration != null) {
        parts.push(
            `<div class="bullet">Daily Bonus <span class="hl">X${Math.round(ctx.effectCoef)}</span> for <span class="hl">${ctx.effectDuration} Days</span></div>`
        );
    }
    if (ctx.noAds) {
        parts.push('<div class="bullet">Removes Forced Ads</div>');
    }
    if (ctx.betcoins > 0) {
        parts.push(`<div class="bullet coin-line">+ ${formatCoins(ctx.betcoins)}<span class="coin-sm">B</span></div>`);
    }
    return parts.join('');
}

function buildBulletsHtml(ctx: ShopHtmlPreviewContext): string {
    if (ctx.displaySection === 'DAILY_DEALS') {
        return buildDailyDealBullets(ctx);
    }
    const lines: string[] = [];
    if (ctx.effectCoef != null && ctx.effectDuration != null) {
        lines.push(`Daily Bonus X${Math.round(ctx.effectCoef)} for ${ctx.effectDuration} Days`);
    } else if (ctx.description && ctx.description !== ctx.title) {
        lines.push(ctx.description);
    }
    if (ctx.noAds) lines.push('Removes forced ads');
    return lines.map(line => `<div class="bullet">${escapeHtml(line)}</div>`).join('');
}

function borderColorFor(ctx: ShopHtmlPreviewContext): string {
    if (ctx.displaySection === 'FIRST_PURCHASE') return '#D4A056';
    if (ctx.badgeType === 'BONUS_PCT_50') return '#9B6DFF';
    if (ctx.badgeType === 'BONUS_PCT_20') return '#E8B84A';
    if (ctx.displaySection === 'DAILY_DEALS') return '#FFB84D';
    if (ctx.displaySection === 'BUNDLES' && ctx.packageDays === 90) return '#C0C0C0';
    if (ctx.displaySection === 'BUNDLES') return '#CD7F32';
    if (ctx.badgeType === 'NEW') return '#C0C0C0';
    return '#CD7F32';
}

function titleAccentFor(ctx: ShopHtmlPreviewContext): string {
    if (ctx.badgeType === 'BONUS_PCT_50') return '#B794FF';
    if (ctx.badgeType === 'BONUS_PCT_20') return '#E8B84A';
    if (ctx.displaySection === 'BUNDLES' && ctx.packageDays === 90) return '#E0E0E8';
    if (ctx.badgeType === 'NEW') return '#E0E0E8';
    if (ctx.displaySection === 'BUNDLES') return '#FFB03A';
    return '#FFB03A';
}

export function shopHtmlAspectRatio(section?: ShopDisplaySection): number {
    switch (section) {
        case 'FIRST_PURCHASE': return 358 / 176;
        case 'DAILY_DEALS': return 360 / 196;
        case 'BUNDLES':
        case 'COIN_PACKS': return 360 / 520;
        case 'FREE_REWARDS': return 360 / 132;
        default: return 360 / 480;
    }
}

export function shopHtmlAspectRatioLabel(section?: ShopDisplaySection): string {
    const width = 360;
    const height = Math.round(width / shopHtmlAspectRatio(section));
    return `${width} / ${height}`;
}

/** Preview height derived from width and design aspect ratio. */
export function shopHtmlCardHeight(section?: ShopDisplaySection, width = 360): number {
    return Math.round(width / shopHtmlAspectRatio(section));
}

export function injectShopHtml(html: string, ctx: ShopHtmlPreviewContext): string {
    const iconForInject = ctx.iconUrl?.trim() && !ctx.iconUrl.startsWith('drawable://')
        ? ctx.iconUrl.trim()
        : null;
    const imageUrl = iconForInject ?? FALLBACK_IMAGE_URL;
    const ribbon = cornerRibbon(ctx.badgeType);
    const isVideo = ctx.isVideoReward === true;
    const ctaDisabled = isVideo && ctx.isVideoRewardAvailable === false;

    const expiresAtMillis = ctx.offerExpiresAtMillis;
    const timerActive = expiresAtMillis != null && expiresAtMillis > Date.now();

    let result = html
        .replace(/\{\{iconUrl\}\}/g, imageUrl)
        .replace(/\{\{oldPrice\}\}/g, ctx.oldPrice || '')
        .replace(/\{\{newPrice\}\}/g, ctx.visualPrice)
        .replace(/\{\{betcoins\}\}/g, formatCoins(ctx.betcoins));

    result = result
        .replace(/\{\{title\}\}/g, escapeHtml(ctx.title))
        .replace(/\{\{eyebrow\}\}/g, escapeHtml(eyebrowFor(ctx)))
        .replace(/\{\{headline\}\}/g, escapeHtml(headlineFor(ctx)))
        .replace(/\{\{subtitle\}\}/g, escapeHtml(subtitleFor(ctx)))
        .replace(/\{\{timer\}\}/g, escapeHtml(formatOfferCountdown(expiresAtMillis) ?? '00:00:00'))
        .replace(/\{\{offerExpiresAtMillis\}\}/g, expiresAtMillis != null ? String(expiresAtMillis) : '0')
        .replace(/\{\{timerDisplay\}\}/g, timerActive ? 'flex' : 'none')
        .replace(/\{\{badge\}\}/g, escapeHtml(badgeLabel(ctx.badgeType)))
        .replace(/\{\{badgeDisplay\}\}/g, ctx.badgeType && !ctx.popular ? 'block' : 'none')
        .replace(/\{\{discount\}\}/g, ctx.discountPercent > 0 ? `-${ctx.discountPercent}%` : '')
        .replace(/\{\{discountDisplay\}\}/g, ctx.discountPercent > 0 ? 'inline' : 'none')
        .replace(/\{\{oldPriceDisplay\}\}/g, ctx.oldPrice ? 'inline' : 'none')
        .replace(/\{\{buyLabel\}\}/g, escapeHtml(ctx.buyLabel))
        .replace(/\{\{storeCode\}\}/g, escapeHtml(ctx.storeCode))
        .replace(/\{\{section\}\}/g, escapeHtml(ctx.displaySection))
        .replace(/\{\{borderColor\}\}/g, borderColorFor(ctx))
        .replace(/\{\{aspectRatio\}\}/g, shopHtmlAspectRatioLabel(ctx.displaySection))
        .replace(/\{\{titleAccent\}\}/g, titleAccentFor(ctx))
        .replace(/\{\{packageDays\}\}/g, ctx.packageDays?.toString() ?? '')
        .replace(/\{\{daysDisplay\}\}/g, ctx.packageDays != null && ctx.packageDays > 0 ? 'flex' : 'none')
        .replace(/\{\{bulletsHtml\}\}/g, buildBulletsHtml(ctx))
        .replace(/\{\{unitPrice\}\}/g, escapeHtml(ctx.unitPriceLabel ?? ''))
        .replace(/\{\{unitPriceDisplay\}\}/g, ctx.unitPriceLabel ? 'flex' : 'none')
        .replace(/\{\{popularRibbonDisplay\}\}/g, ctx.popular && ctx.displaySection === 'DAILY_DEALS' ? 'block' : 'none')
        .replace(/\{\{cornerRibbonText\}\}/g, escapeHtml(ribbon.text))
        .replace(/\{\{cornerRibbonDisplay\}\}/g, ribbon.visible ? 'block' : 'none')
        .replace(/\{\{cornerRibbonClass\}\}/g, ribbon.cssClass)
        .replace(/\{\{heroDisplay\}\}/g, iconForInject ? 'block' : 'none')
        .replace(/\{\{cardOpacity\}\}/g, ctaDisabled ? '0.45' : '1')
        .replace(/\{\{ctaDisabled\}\}/g, ctaDisabled ? 'true' : 'false')
        .replace(/\{\{videoTitle\}\}/g, escapeHtml(ctx.videoTitle ?? ''))
        .replace(/\{\{videoSubtitle\}\}/g, escapeHtml(ctx.videoSubtitle ?? ''))
        .replace(/\{\{ctaAction\}\}/g, isVideo
            ? 'watchVideo()'
            : `purchase('${escapeJs(ctx.storeCode)}')`);

    return injectShopResponsiveHtml(result);
}
